import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch } from "../config/api";
import { setSession } from "../config/auth";

const SUITE = [
  { label: "Work Management", desc: "Tasks, projects, MOMs & workflows", color: "#0b72b9" },
  { label: "CRM", desc: "Pipeline, contacts & deal tracking", color: "#2563eb" },
  { label: "HRMS", desc: "Employees, attendance & leaves", color: "#059669" },
  { label: "Finance & Admin", desc: "Invoices, budgets & approvals", color: "#d97706" },
  { label: "Client Portal", desc: "Projects, requests & approvals", color: "#7c3aed" },
];

const STATS = [
  { value: "5+", label: "Modules" },
  { value: "All-in-one", label: "Platform" },
  { value: "Real-time", label: "Collaboration" },
];

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export default function LoginPage({ onLogin }) {
  const [tenantSlug, setTenantSlug] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();

  const login = async () => {
    if (loading) return;
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setError("");
    try {
      setLoading(true);
      const res = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({
          tenantSlug: tenantSlug.trim() || null,
          email,
          password,
        }),
      });
      if (!res.ok) {
        setError("Invalid email or password. Please try again.");
        return;
      }
      const data = await res.json();
      setSession({ user: data.user, accessToken: data.accessToken, refreshToken: data.refreshToken });
      onLogin?.();
      navigate("/apps");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (field) => ({
    width: "100%",
    background: focusedField === field ? "#fff" : "#f8fafc",
    border: `1.5px solid ${focusedField === field ? "#0b72b9" : "#d5dbe6"}`,
    color: "#111827",
    borderRadius: 10,
    padding: "11px 14px",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 0.15s, background 0.15s",
    boxShadow: focusedField === field ? "0 0 0 3px rgba(11,114,185,0.12)" : "none",
  });

  return (
    <div style={s.root}>
      {/* ── Grid background ── */}
      <div style={s.grid} />

      <div style={s.wrapper}>

        {/* ── Left: Dark hero panel ── */}
        <section style={s.hero}>
          {/* Inner grid pattern */}
          <div style={s.heroGrid} />

          <div style={s.heroContent}>
            {/* Brand */}
            <div style={s.logoRow}>
              <img src="/logo.svg" alt="Tackl" style={{ width: 36, height: 36, objectFit: "contain" }} />
              <div style={s.wordmark}>Tackl Suite</div>
            </div>

            {/* Hero text */}
            <div style={s.heroText}>
              <p style={s.heroLabel}>One platform. Every team.</p>
              <h1 style={s.heroHeading}>
                Run your entire<br />business from one place.
              </h1>
              <p style={s.heroDesc}>
                Work management, CRM, HR, Finance and more — all connected, built for growing teams.
              </p>
            </div>

            {/* Stats */}
            <div style={s.statsRow}>
              {STATS.map((stat, i) => (
                <div key={stat.label} style={{ ...s.statBox, borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                  <div style={s.statValue}>{stat.value}</div>
                  <div style={s.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Module list */}
            <div style={s.moduleList}>
              <p style={s.moduleListTitle}>Included in your workspace</p>
              <div style={s.moduleGrid}>
                {SUITE.map((m) => (
                  <div key={m.label} style={s.moduleItem}>
                    <span style={{ ...s.moduleCheck, background: `${m.color}22`, color: m.color }}>
                      <CheckIcon />
                    </span>
                    <div>
                      <div style={s.moduleLabel}>{m.label}</div>
                      <div style={s.moduleDesc}>{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Right: Login form ── */}
        <section style={s.formSection}>
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h2 style={s.cardTitle}>Sign in to your workspace</h2>
              <p style={s.cardSub}>Enter your credentials to continue.</p>
            </div>

            {error && (
              <div style={s.errorBox}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth={2} strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {error}
              </div>
            )}

            <div style={s.form}>
              <div style={s.field}>
                <label style={s.label}>WORKSPACE</label>
                <input
                  type="text"
                  placeholder="default"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  onFocus={() => setFocusedField("workspace")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle("workspace")}
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>EMAIL</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle("email")}
                />
              </div>

              <div style={s.field}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={s.label}>PASSWORD</label>
                  <Link to="/forgot-password" style={s.forgotLink}>Forgot password?</Link>
                </div>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && login()}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle("password")}
                />
              </div>

              <button
                onClick={login}
                disabled={loading}
                style={{
                  ...s.submitBtn,
                  opacity: loading ? 0.75 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ animation: "spin 0.8s linear infinite" }}>
                      <circle cx="12" cy="12" r="10" strokeOpacity={0.25} />
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    Signing in…
                  </span>
                ) : (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                      <path d="M15 12H3m12 0l-4-4m4 4l-4 4m6-10h2a2 2 0 012 2v8a2 2 0 01-2 2h-2" />
                    </svg>
                    Sign in
                  </span>
                )}
              </button>
            </div>
          </div>

          <p style={s.footer}>© 2026 Tackl. All rights reserved.</p>
        </section>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "stretch",
    background: "#F8F7F5",
    fontFamily: "'Segoe UI', 'Plus Jakarta Sans', system-ui, sans-serif",
    position: "relative",
  },
  grid: {
    position: "fixed",
    inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(17,24,39,0.04) 1px, transparent 1px)",
    backgroundSize: "28px 28px",
    pointerEvents: "none",
    zIndex: 0,
  },

  wrapper: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr",
    width: "100%",
    minHeight: "100vh",
    gap: 0,
  },

  /* Hero panel */
  hero: {
    position: "relative",
    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 55%, #0f172a 100%)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  heroGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  heroContent: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "40px 48px",
  },

  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  wordmark: {
    fontSize: 18,
    fontWeight: 800,
    color: "#f8fafc",
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
  },
  heroText: {
    padding: "32px 0",
  },
  heroLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#0b72b9",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    marginBottom: 14,
  },
  heroHeading: {
    fontSize: 44,
    fontWeight: 800,
    color: "#f8fafc",
    lineHeight: 1.08,
    letterSpacing: "-0.025em",
    marginBottom: 16,
  },
  heroDesc: {
    fontSize: 14,
    color: "#94a3b8",
    lineHeight: 1.7,
    maxWidth: 420,
  },

  statsRow: {
    display: "flex",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(255,255,255,0.04)",
    overflow: "hidden",
  },
  statBox: {
    flex: 1,
    padding: "16px 20px",
  },
  statValue: {
    fontSize: 20,
    fontWeight: 800,
    color: "#f1f5f9",
    letterSpacing: "-0.02em",
    lineHeight: 1,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },

  moduleList: {
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(0,0,0,0.2)",
    padding: "20px 22px",
  },
  moduleListTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    marginBottom: 14,
  },
  moduleGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  moduleItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  },
  moduleCheck: {
    width: 20,
    height: 20,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 1,
  },
  moduleLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#e2e8f0",
    lineHeight: 1.3,
  },
  moduleDesc: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 1,
  },

  /* Form section */
  formSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 32px",
    gap: 16,
  },
  card: {
    width: "100%",
    maxWidth: 400,
    background: "#ffffff",
    border: "1px solid #dfe3ea",
    borderRadius: 16,
    padding: "36px 34px 32px",
    boxShadow: "0 4px 24px rgba(16,24,40,0.08), 0 1px 3px rgba(16,24,40,0.06)",
  },
  cardHeader: {
    marginBottom: 26,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: "#111827",
    letterSpacing: "-0.015em",
    lineHeight: 1.2,
    marginBottom: 6,
  },
  cardSub: {
    fontSize: 13,
    color: "#6b7280",
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 10,
    background: "#fff1f2",
    border: "1px solid rgba(220,38,38,0.18)",
    color: "#991b1b",
    fontSize: 13,
    marginBottom: 16,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.12em",
    color: "#6b7280",
  },
  forgotLink: {
    fontSize: 12,
    color: "#0b72b9",
    textDecoration: "none",
    fontWeight: 600,
  },
  submitBtn: {
    marginTop: 4,
    width: "100%",
    padding: "13px",
    fontSize: 14,
    fontWeight: 700,
    background: "linear-gradient(135deg, #0b72b9, #075c96)",
    color: "white",
    border: "none",
    borderRadius: 10,
    boxShadow: "0 4px 14px rgba(11,114,185,0.28)",
    letterSpacing: "0.01em",
    fontFamily: "inherit",
    transition: "opacity 0.15s",
  },
  footer: {
    fontSize: 12,
    color: "#9ca3af",
    textAlign: "center",
  },
};
